// Centralised write helpers for the Stock surface.
//
// SOH is manual-only. The On-Hand drawer ("Update stock") is the ONLY
// path that mutates stock_on_hand — via writeStockAdjustment below.
// Receiving (the Incoming drawer "Log receipt" + the inline "Mark as
// Received" quick action) records the receipt and arrival progress ONLY;
// it does NOT touch stock_on_hand. Operators reconcile physical stock
// manually from the On-Hand drawer after a delivery is checked in.
//
// Source of truth for SOH math:        `forecaster_stock_adjustments`.
// Source of truth for receipt history: `forecaster_receipts`.
//
// The legacy fields (`forecaster_incoming_stock.received_qty/at/by`,
// `packing_slip_path`, `coa_path`) are mirrored from these helpers for
// one release. Drop those columns once the UI is fully on the ledgers.

import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';

const REASONS = new Set(['count', 'received', 'damaged', 'correction', 'transfer']);
const ATTACHMENT_KINDS = new Set([
  'packing_slip', 'pod', 'invoice', 'photo',
  // Phase 2 — batch-level compliance docs (mig 142 widened the CHECK).
  // Keep byte-identical with the migration CHECK and IncomingDrawer's
  // ATTACHMENT_KINDS labels.
  'coa', 'compliance_cert', 'batch_cert', 'other_report',
]);

/**
 * Apply a stock-on-hand adjustment.
 *
 * Writes (in order):
 *   1. forecaster_skus.stock_on_hand        ← new SOH value
 *   2. forecaster_stock_adjustments         ← typed audit row
 *   3. forecaster_activity                  ← free-text mirror for the
 *                                              general timeline
 *
 * @param {object} session — the auth session ({ name, role, ... })
 * @param {object} args
 * @param {string} args.skuId         — forecaster_skus.id
 * @param {string} args.skuName       — for the activity log free text
 * @param {number} args.delta         — signed change in units
 * @param {number} args.newOnhand     — final stock_on_hand value
 * @param {string} args.reason        — must be one of REASONS
 * @param {string} [args.note]
 * @param {string} [args.supplierName] — only when reason === 'received'
 * @param {string} [args.poRef]       — only when reason === 'received'
 */
export async function writeStockAdjustment(session, args) {
  if (!REASONS.has(args.reason)) {
    throw new Error(`Invalid stock-adjustment reason: ${args.reason}`);
  }

  // 1. Bump SOH.
  await authWrite(
    session,
    'forecaster_skus',
    'update',
    { stock_on_hand: args.newOnhand, updated_by: session.name },
    { column: 'id', value: args.skuId },
  );

  // 2. Append to typed ledger.
  await authWrite(session, 'forecaster_stock_adjustments', 'insert', {
    sku_id: args.skuId,
    delta: args.delta,
    new_onhand: args.newOnhand,
    reason: args.reason,
    supplier_name: args.supplierName ?? null,
    note: args.note ?? null,
    po_ref: args.poRef ?? null,
    actor_name: session.name,
  });

  // 3. Free-text mirror — keeps the general activity feed working.
  // forecaster_activity stays bare: it IS the audit trail; routing it
  // through authWrite would double-log every stock movement.
  await aws.from('forecaster_activity').insert([{
    actor_name: session.name,
    action: 'Stock adjusted',
    detail: `${args.skuName ?? args.skuId}: ${args.delta > 0 ? '+' : ''}${args.delta} (${args.reason}) → ${args.newOnhand}`,
  }]);
}

/**
 * Log a (possibly partial) receipt against an open PO.
 *
 * Receiving records arrival progress ONLY — it does NOT change SOH.
 * Stock-on-hand is reconciled manually from the On-Hand drawer.
 *
 * Writes (in order):
 *   1. forecaster_receipts                  ← new ledger row (source of
 *                                              truth for receipt history)
 *   2. forecaster_incoming_stock            ← legacy mirror: bumps
 *                                              received_qty (sum of all
 *                                              receipts), sets received_at,
 *                                              received_by
 *
 * Caller passes the PO's full row so we can compute the cumulative
 * received_qty without re-fetching.
 *
 * @param {object} session
 * @param {object} args
 * @param {object} args.po              — the forecaster_incoming_stock row
 * @param {number} args.qty             — units received this time
 * @param {boolean} args.isPartial      — caller's view; ledger flag only
 * @param {string} [args.note]
 * @param {string} [args.attachmentPath] — optional inline attachment
 *                                          (separate from the typed
 *                                          attachments — that's
 *                                          writeIncomingAttachment)
 */
export async function writeReceipt(session, args) {
  const { po, qty, isPartial, note, attachmentPath } = args;
  if (!Number.isFinite(qty) || qty <= 0) throw new Error('Receipt qty must be > 0');

  // 1. New ledger.
  await authWrite(session, 'forecaster_receipts', 'insert', {
    incoming_id: po.id,
    qty,
    is_partial: !!isPartial,
    note: note ?? null,
    attachment_path: attachmentPath ?? null,
    received_by: session.name,
  });

  // 2. Legacy mirror — sum prior receipts so received_qty stays correct.
  const newReceivedQty = Number(po.received_qty || 0) + qty;
  await authWrite(session, 'forecaster_incoming_stock', 'update', {
    received_qty: newReceivedQty,
    received_at: new Date().toISOString(),
    received_by: session.name,
  }, { column: 'id', value: po.id });

  // NOTE: receiving deliberately does NOT touch stock_on_hand. SOH is
  // manual-only and is reconciled from the On-Hand drawer. (Was: a
  // writeStockAdjustment(reason='received') call here that bumped SOH —
  // removed in Phase 2 so "Received" never mutates stock on hand.)
}

/**
 * Log a typed attachment for an incoming PO. Writes only the attachment
 * row — file upload to S3 is the caller's responsibility (use
 * `app/src/lib/awsStorage` against the existing `po-attachments` bucket
 * and pass the resulting key in as `path`).
 *
 * `batchNumber` is optional batch-level metadata (a snapshot of the PO's
 * forecaster_incoming_stock.batch_number at upload time) so compliance
 * docs (COA / certificates) can show which batch they certify.
 */
export async function writeIncomingAttachment(session, { incomingId, kind, path, batchNumber = null }) {
  if (!ATTACHMENT_KINDS.has(kind)) {
    throw new Error(`Invalid attachment kind: ${kind}`);
  }
  await authWrite(session, 'forecaster_incoming_attachments', 'insert', {
    incoming_id: incomingId,
    kind,
    path,
    batch_number: batchNumber ?? null,
    uploaded_by: session.name,
  });
}

const CLOSE_REASONS = new Set(['short_received']);

/**
 * Explicitly close an incoming PO row as final / short-received — used
 * when a PARTIAL delivery is the last one (no more coming). Sets
 * closed_at + close_reason so the Incoming list hides it while the
 * variance (qty - received_qty) stays derivable. Does NOT touch SOH.
 */
export async function closeIncoming(session, { incomingId, reason = 'short_received', skuName = null }) {
  if (!CLOSE_REASONS.has(reason)) {
    throw new Error(`Invalid close reason: ${reason}`);
  }
  await authWrite(session, 'forecaster_incoming_stock', 'update', {
    closed_at: new Date().toISOString(),
    close_reason: reason,
  }, { column: 'id', value: incomingId });
  await aws.from('forecaster_activity').insert([{
    actor_name: session.name,
    action: 'Incoming closed (short-received)',
    detail: `${skuName ?? incomingId} — closed as final / short-received`,
  }]);
}

/**
 * Reopen an incoming PO row closed short by mistake (or because more
 * stock arrived after all). Clears closed_at + close_reason so the row
 * returns to the Incoming list. Does NOT touch SOH.
 */
export async function reopenIncoming(session, { incomingId, skuName = null }) {
  await authWrite(session, 'forecaster_incoming_stock', 'update', {
    closed_at: null,
    close_reason: null,
  }, { column: 'id', value: incomingId });
  await aws.from('forecaster_activity').insert([{
    actor_name: session.name,
    action: 'Incoming reopened',
    detail: `${skuName ?? incomingId} — reopened (short-close reversed)`,
  }]);
}
